// ADE Logo Icon Component - Uses the official PNG logo
// For AdE Like: grayscale when not liked, colored when liked
// NOTE: Logo has 3:2 aspect ratio (width:height = 1.5:1)

export const AdeLogoIcon = ({ 
  size = 80,  // This is the WIDTH of the logo
  className = "",
  grayscale = false,
  showSparkle = true
}) => {
  // Calculate height based on 3:2 aspect ratio
  const height = Math.round(size / 1.5);
  
  return (
    <div 
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: height }}
    >
      <img 
        src="/images/ade-logo-extracted.png" 
        alt="ADE"
        style={{ 
          width: size,
          height: height,
          objectFit: 'contain',
          filter: grayscale ? 'grayscale(100%) brightness(0.6)' : 'none',
          transition: 'all 0.3s ease'
        }}
      />
      {/* Sparkle overlay when active */}
      {showSparkle && !grayscale && (
        <div className="absolute -top-1 -right-1 w-2 h-2">
          <div className="absolute inset-0 bg-orange-400 rounded-full animate-ping opacity-75" />
        </div>
      )}
    </div>
  );
};

// AdE Like button using official ADE logo
export const AdLikeIcon = ({ 
  filled = false, 
  size = 32, 
  className = "",
  onClick,
  "data-testid": dataTestId
}) => {
  return (
    <button 
      onClick={onClick}
      data-testid={dataTestId}
      aria-pressed={filled}
      className={`relative group transition-transform active:scale-90 hover:scale-110 ${className}`}
      type="button"
      style={{ overflow: 'visible' }}
    >
      {/* Base ADE Logo - same size as other icons */}
      <AdeLogoIcon 
        size={size} 
        grayscale={!filled} 
        showSparkle={false}
      />
      
      {/* LARGE sparkle/star effects that OVERFLOW outside the icon */}
      {filled && (
        <div className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
          {/* Top sparkle - large and outside */}
          <span 
            className="absolute bg-orange-400 rounded-full animate-ping"
            style={{ 
              width: '10px', 
              height: '10px', 
              top: '-8px', 
              left: '50%', 
              transform: 'translateX(-50%)',
              boxShadow: '0 0 8px rgba(251, 146, 60, 0.8)'
            }} 
          />
          {/* Top-right sparkle */}
          <span 
            className="absolute bg-pink-500 rounded-full animate-ping"
            style={{ 
              width: '8px', 
              height: '8px', 
              top: '-4px', 
              right: '-6px',
              animationDelay: '150ms',
              boxShadow: '0 0 6px rgba(236, 72, 153, 0.8)'
            }} 
          />
          {/* Top-left sparkle */}
          <span 
            className="absolute bg-purple-500 rounded-full animate-ping"
            style={{ 
              width: '8px', 
              height: '8px', 
              top: '-4px', 
              left: '-6px',
              animationDelay: '300ms',
              boxShadow: '0 0 6px rgba(168, 85, 247, 0.8)'
            }} 
          />
          {/* Right sparkle */}
          <span 
            className="absolute bg-yellow-400 rounded-full animate-ping"
            style={{ 
              width: '7px', 
              height: '7px', 
              top: '50%', 
              right: '-8px',
              transform: 'translateY(-50%)',
              animationDelay: '100ms',
              boxShadow: '0 0 6px rgba(250, 204, 21, 0.8)'
            }} 
          />
          {/* Left sparkle */}
          <span 
            className="absolute bg-orange-500 rounded-full animate-ping"
            style={{ 
              width: '7px', 
              height: '7px', 
              top: '50%', 
              left: '-8px',
              transform: 'translateY(-50%)',
              animationDelay: '200ms',
              boxShadow: '0 0 6px rgba(249, 115, 22, 0.8)'
            }} 
          />
          {/* Bottom sparkles */}
          <span 
            className="absolute bg-pink-400 rounded-full animate-ping"
            style={{ 
              width: '6px', 
              height: '6px', 
              bottom: '-6px', 
              left: '30%',
              animationDelay: '250ms',
              boxShadow: '0 0 5px rgba(244, 114, 182, 0.8)'
            }} 
          />
          <span 
            className="absolute bg-purple-400 rounded-full animate-ping"
            style={{ 
              width: '6px', 
              height: '6px', 
              bottom: '-6px', 
              right: '30%',
              animationDelay: '350ms',
              boxShadow: '0 0 5px rgba(192, 132, 252, 0.8)'
            }} 
          />
        </div>
      )}
    </button>
  );
};

// Small ADE logo for displaying in feeds, counts, etc.
// Maintains original aspect ratio (3:2, width:height)
export const AdeLogoSmall = ({ size = 24, className = "" }) => {
  const height = Math.round(size / 1.5);
  return (
    <img 
      src="/images/ade-logo-extracted.png" 
      alt="ADE"
      className={className}
      style={{ 
        width: size,
        height: height,
        objectFit: 'contain'
      }}
    />
  );
};

// Location Pin with glow effect (for onboarding)
export const GlowingPin = ({ 
  size = 40, 
  className = "",
  delay = 0 
}) => (
  <div 
    className={`relative animate-float ${className}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Outer glow */}
    <div 
      className="absolute inset-0 rounded-full blur-xl opacity-60"
      style={{
        background: 'radial-gradient(circle, #FF8C00 0%, #FF00CC 50%, transparent 70%)',
        transform: 'scale(2)'
      }}
    />
    
    <svg viewBox="0 0 24 32" width={size} height={size * 1.33} className="relative z-10 drop-shadow-2xl">
      <defs>
        <linearGradient id="pin-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF8C00" />
          <stop offset="50%" stopColor="#FF00CC" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
        <filter id="pin-glow">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <path 
        d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z" 
        fill="url(#pin-grad)"
        filter="url(#pin-glow)"
      />
      <circle cx="12" cy="10" r="4" fill="rgba(255,255,255,0.3)" />
      <circle cx="11" cy="9" r="2" fill="rgba(255,255,255,0.6)" />
    </svg>
  </div>
);

export const AdeLikeGlyph = ({ className = "" }) => (
  <img src="/images/ade-logo-extracted.png" alt="like" className={`object-contain ${className}`} />
);

export default AdeLogoIcon;
